<template>
  <div class="knowledge-page">
    <div class="main-content">
      <!-- 第一部分：英雄区域 -->
      <section class="hero-section">
        <div class="container">
          <AppHeader />
          
          <div class="hero-content">
            <h1 class="hero-title animate">闻讼知识库</h1>
            <h2 class="hero-subtitle animate">以知识助力专业技能提升</h2>
          </div>
        </div>
      </section>
      
      <!-- 第二部分：知识库列表 -->
      <section class="knowledge-list-section" ref="knowledgeListSection">
        <div class="container">
          <h2 class="section-title animate-scroll">闻讼法律知识库</h2>
          
          <div class="knowledge-list">
            <div v-show="loading" class="loading">加载中...</div>
            <div v-show="!loading && articles.length === 0" class="no-data">
              暂无文章数据
            </div>
            
            <!-- 文章列表,使用更简单的结构 -->
            <div v-show="!loading && articles.length > 0" class="knowledge-list-container">
              <div class="knowledge-item" 
                   v-for="(article, index) in articles" 
                   :key="article.id"
                   :style="{'--delay': `${0.1 * index}s`}"
                   @click="goToArticleDetail(article.id)">
                <div class="knowledge-image">
                  <img :src="article.cover || require('@/assets/image/knowledge-bg@2x.png')" :alt="article.title">
                </div>
                <div class="knowledge-content">
                  <div class="knowledge-date">{{formatDate(article.publish_time)}}</div>
                  <div class="knowledge-title">{{article.title}}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 分页 -->
          <div v-show="!loading && articles.length > 0" class="pagination animate-scroll">
            <a href="javascript:void(0)" class="page-link" @click="prevPage" :class="{ disabled: params.page <= 1 }">&lt;</a>
            
            <template v-for="p in displayPageNumbers" :key="p">
              <a v-if="p === '...'" class="page-link">...</a>
              <a v-else href="javascript:void(0)" 
                 class="page-link" 
                 :class="{ active: params.page === p }" 
                 @click="changePage(p)">{{p}}</a>
            </template>
            
            <a href="javascript:void(0)" class="page-link" @click="nextPage" :class="{ disabled: params.page >= totalPages }">&gt;</a>
          </div>
        </div>
      </section>
    </div>
    <div class="footer-wrapper">
      <AppFooter />
    </div>
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue';
import AppHeader from '@/components/AppHeader.vue';
import { getArticleList } from '@/api';
import { RESPONSE_CODE } from '@/api/constants';

export default {
  name: 'KnowledgePage',
  components: {
    AppFooter,
    AppHeader
  },
  data() {
    return {
      observers: [],
      articles: [],
      loading: false,
      params: {
        page: 1,
        limit: 8 // 每页显示8篇文章
      },
      total: 0, // 文章总数
    }
  },
  computed: {
    // 总页数
    totalPages() {
      return Math.ceil(this.total / this.params.limit) || 1;
    },
    // 显示的页码
    displayPageNumbers() {
      const current = this.params.page;
      const total = this.totalPages;
      const delta = 1; // 当前页前后显示的页码数
      
      let pages = [];
      
      // 始终显示第一页
      pages.push(1);
      
      // 当前页前面的页码
      if (current - delta > 2) {
        pages.push('...'); // 如果当前页与第一页相差超过delta+1，显示省略号
      }
      
      let start = Math.max(2, current - delta);
      let end = Math.min(total - 1, current + delta);
      
      // 显示当前页附近的页码
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // 当前页后面的页码
      if (current + delta < total - 1) {
        pages.push('...'); // 如果当前页与最后页相差超过delta+1，显示省略号
      }
      
      // 始终显示最后页
      if (total > 1) {
        pages.push(total);
      }
      
      return pages;
    }
  },
  mounted() {
    this.initScrollAnimations();
    
    // 首屏动画自动播放
    const animateElements = document.querySelectorAll('.animate');
    animateElements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('visible');
      }, 300 * index);
    });
    
    // 获取URL中的页码参数
    const pageParam = parseInt(this.$route.query.page);
    if (pageParam && !isNaN(pageParam) && pageParam > 0) {
      this.params.page = pageParam;
    }
    
    // 获取文章列表
    this.fetchArticles();

    // 调试代码：监控articles变化
    this.$watch('articles', (newArticles) => {
      console.log('articles变化:', newArticles);
      // 延迟检查DOM元素
      this.$nextTick(() => {
        const items = document.querySelectorAll('.knowledge-item');
        console.log('DOM中的文章项数量:', items.length);
        
        // 检查可见性
        items.forEach((item, index) => {
          console.log(`文章项 ${index} 可见性:`, 
            window.getComputedStyle(item).display, 
            window.getComputedStyle(item).visibility,
            window.getComputedStyle(item).opacity
          );
        });
      });
    });

    // 头部动画
    this.$nextTick(() => {
      const header = this.$el.querySelector('.logo-area.animate-scroll');
      if (header) header.classList.add('visible');
    });
  },
  beforeUnmount() {
    // 清除所有观察者
    this.observers.forEach(observer => observer.disconnect());
  },
  methods: {
    // 获取文章列表
    async fetchArticles() {
      this.loading = true;
      console.log('开始获取文章列表, 参数:', this.params);
      try {
        const res = await getArticleList(this.params);
        console.log('接口返回数据:', res); // 添加调试日志
        if (res.code === RESPONSE_CODE.SUCCESS_ALT || res.code === RESPONSE_CODE.SUCCESS) {
          // 适配接口返回的不同数据结构
          let articlesData = [];
          let totalCount = 0;

          // 处理两种可能的数据结构
          if (res.data && res.data.data && Array.isArray(res.data.data)) {
            // 第一种结构: res.data.data 是数组
            articlesData = res.data.data;
            totalCount = res.data.total || 0;
            console.log('使用数据结构1: res.data.data');
          } else if (res.data && Array.isArray(res.data)) {
            // 第二种结构: res.data 直接是数组
            articlesData = res.data;
            totalCount = res.total || articlesData.length || 0;
            console.log('使用数据结构2: res.data');
          } else {
            console.warn('未能识别的数据结构:', res);
          }

          console.log('原始文章数据:', articlesData);
          console.log('文章总数:', totalCount);

          // 映射字段
          this.articles = articlesData.map(item => ({
            id: item.id,
            title: item.title || '无标题',
            // 处理多种可能的封面图字段
            cover: item.cover_image || item.cover || item.image || '',
            // 处理多种可能的时间字段
            publish_time: item.createtime || item.create_time || item.publish_time || ''
          }));
          
          console.log('转换后的文章数据:', this.articles);
          this.total = totalCount;
          
          // 更新URL，不刷新页面
          this.updatePageUrl();

          // 手动强制DOM更新
          this.$forceUpdate();
          
          // 延迟检查DOM
          setTimeout(() => {
            console.log('延迟检查DOM');
            const items = document.querySelectorAll('.knowledge-item');
            console.log('DOM中的文章项数量:', items.length);
            const rows = document.querySelectorAll('.knowledge-row');
            console.log('DOM中的行数量:', rows.length);
          }, 500);
        } else {
          console.error('获取文章列表失败:', res.msg);
        }
      } catch (error) {
        console.error('获取文章列表失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '';
      
      try {
        // 处理时间戳格式
        if (!isNaN(dateStr)) {
          // 如果是10位时间戳，转为13位
          if (dateStr.toString().length === 10) {
            dateStr = parseInt(dateStr) * 1000;
          }
          const date = new Date(parseInt(dateStr));
          return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
        }
        
        // 处理字符串格式
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          // 尝试处理格式为 "yyyy-MM-dd HH:mm:ss" 的日期
          const parts = dateStr.split(/[- :]/);
          if (parts.length >= 3) {
            const date = new Date(parts[0], parts[1] - 1, parts[2]);
            return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
          }
          return dateStr; // 无法解析则返回原字符串
        }
        
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
      } catch (error) {
        console.error('日期格式化错误:', error, dateStr);
        return dateStr; // 出错时返回原字符串
      }
    },
    
    // 跳转到文章详情页
    goToArticleDetail(id) {
      this.$router.push(`/article/${id}`);
    },
    
    // 切换到指定页码
    changePage(page) {
      if (this.params.page === page) return;
      this.params.page = page;
      this.fetchArticles();
      // 滚动到内容区域顶部
      this.$refs.knowledgeListSection.scrollIntoView({ behavior: 'smooth' });
    },
    
    // 上一页
    prevPage() {
      if (this.params.page > 1) {
        this.changePage(this.params.page - 1);
      }
    },
    
    // 下一页
    nextPage() {
      if (this.params.page < this.totalPages) {
        this.changePage(this.params.page + 1);
      }
    },
    
    // 更新URL中的页码参数
    updatePageUrl() {
      if (this.params.page === 1) {
        this.$router.replace({ query: {} });
      } else {
        this.$router.replace({ query: { page: this.params.page } });
      }
    },
    
    // 初始化滚动动画
    initScrollAnimations() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      const sections = [
        this.$refs.knowledgeListSection
      ];
      
      sections.forEach(section => {
        if (section) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const animatedElements = entry.target.querySelectorAll('.animate-scroll');
                animatedElements.forEach(el => {
                  el.classList.add('visible');
                });
              }
            });
          }, options);
          
          observer.observe(section);
          this.observers.push(observer);
        }
      });
    }
  }
}
</script>

<style scoped>
.knowledge-page {
  margin: 0;
  padding: 0;
  font-family: Arial, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* 英雄区域样式 */
.hero-section {
  width: 100%;
  height: 600px;
  background-image: url('~@/assets/image/zskbg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.container {
  width: 1200px;
  margin: 0 auto;
  position: relative;
}

.logo-area {
  padding-top: 20px;
}

.logo {
  width: 109px;
  height: 40px;
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 322px;
}

.hero-title {
  font-weight: 600;
  font-size: 48px;
  color: #FFFFFF;
  margin: 0;
}

.hero-subtitle {
  font-weight: 400;
  font-size: 24px;
  color: #FFFFFF;
  margin-top: 20px;
}

.divider {
  width: 30px;
  height: 4px;
  background: #FFFFFF;
  margin: 20px 0;
}

/* 知识库列表部分样式 */
.knowledge-list-section {
  width: 100%;
  background: #FFFFFF;
  padding: 60px 0;
  position: relative;
  overflow: hidden;
}

.section-title {
  font-weight: 600;
  font-size: 36px;
  color: #333333;
  text-align: left;
  margin-bottom: 40px;
}

.knowledge-list {
  margin-top: 40px;
}

.knowledge-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.knowledge-list-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.knowledge-item {
  width: 580px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.5s ease;
  cursor: pointer;
  animation: fadeIn 0.5s ease forwards;
  animation-delay: var(--delay, 0s);
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.knowledge-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.knowledge-image {
  width: 180px;
  height: 130px;
  overflow: hidden;
  flex-shrink: 0;
}

.knowledge-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.knowledge-content {
  flex-grow: 1;
  padding: 20px;
}

.knowledge-date {
  font-size: 14px;
  color: #999999;
  margin-bottom: 10px;
}

.knowledge-title {
  font-size: 18px;
  color: #333333;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 60px;
}

.page-link {
  width: 36px;
  height: 36px;
  border: 1px solid #DDDDDD;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 5px;
  color: #333333;
  text-decoration: none;
  font-size: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.page-link.active {
  background-color: #3578E5;
  color: #FFFFFF;
  border-color: #3578E5;
}

.page-link:hover:not(.active):not(.disabled) {
  border-color: #3578E5;
  color: #3578E5;
}

.page-link.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 加载状态和无数据状态 */
.loading, .no-data {
  text-align: center;
  padding: 40px 0;
  font-size: 16px;
  color: #999;
}

/* 入场动画基础样式 */
.animate, .animate-scroll {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 1s ease, transform 1s ease;
}

.animate:not(.visible) {
  opacity: 0;
  transform: translateY(30px);
}

/* 响应式容器 */
/* @media screen and (max-width: 1300px) {
  .container {
    width: 90%;
    padding: 0 15px;
  }
}

@media screen and (max-width: 768px) {
  .container {
    width: 95%;
  }
  
  .knowledge-row {
    flex-direction: column;
  }
  
  .knowledge-item {
    width: 100%;
    margin-bottom: 20px;
  }
} */

.footer-wrapper {
  flex-shrink: 0;
}
</style> 